import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '~/redux/root-store';
import {useInput, useNotification} from '~/hooks';
import {Col, UiImage, UiText, UiQuestionnaireLayout, UiBox, UiSimpleGrid, UiButton} from '~/components/elements';
import {RootState} from '~/redux/root-reducer';
import {PERIOD_CONTROL_MEDICATION_KEYS} from '~/constants/medical-questionnaires';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Option} from '~/redux/questionnaires/types';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire4Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[PERIOD_CONTROL_MEDICATION_KEYS.MEDICAL_HISTORY]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === PERIOD_CONTROL_MEDICATION_KEYS.MEDICAL_HISTORY);

  let medicalOptions: Option[] = [];
  if (questionnaire?.options?.length) {
    medicalOptions = questionnaire?.options?.filter(i => i?.value !== 'NONE');
  }

  const defaultOptions = medicalOptions.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    if (currentAnswer?.selectedOption?.length) {
      currentAnswer?.selectedOption.map((i: string) => {
        if (i !== 'NONE') res[i] = true;
      });
    }

    return res;
  }, {});

  const resetOptions = medicalOptions.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    return res;
  }, {});

  const {inputs, setInputs} = useInput({
    defaultOptions,
    noneOptions: (currentAnswer?.selectedOption?.length && currentAnswer?.selectedOption[0] === 'NONE') || false,
  });
  const dataSubmit = Object.keys(inputs.defaultOptions).filter(i => inputs.defaultOptions[i] === true);

  const onClickOption = (answer: {value: string | number; label: string}) => {
    const {value} = answer;
    setInputs({noneOptions: false, defaultOptions: {...inputs.defaultOptions, [value]: !inputs.defaultOptions[value]}});
  };

  const onClickNoneOption = () => {
    setInputs({noneOptions: !inputs.noneOptions, defaultOptions: resetOptions});
  };

  const onClickSubmit = async () => {
    dispatch(
      setQuestionnaireAnswer({
        key: PERIOD_CONTROL_MEDICATION_KEYS.MEDICAL_HISTORY,
        index: questionnaire?.index || '',
        data: {selectedOption: inputs?.noneOptions ? ['NONE'] : dataSubmit},
      }),
    );
    dispatch(setNextQuestionnaire({value: PERIOD_CONTROL_MEDICATION_KEYS.HAD_BLOOD_PRESSURE_MEASURED_LAST_6_MONTH}));
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!dataSubmit.length && !inputs.noneOptions}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.medium} w="100%">
          <UiSimpleGrid columns={{base: 1, lg: 2}} spacing={5}>
            {medicalOptions?.map(value => (
              <UiButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.defaultOptions[value.value] ? 'solid' : 'toggle'}
                onClick={() => onClickOption(value)}
              />
            ))}
          </UiSimpleGrid>
          <UiBox mt={spacing.small} display="flex" alignItems="center" justifyContent="center" w="100%">
            <UiButton
              w="100%"
              px={spacing.medium}
              title="None of the above"
              variant={inputs.noneOptions ? 'solid' : 'toggle'}
              onClick={onClickNoneOption}
            />
          </UiBox>
        </UiBox>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire4Screen;
